import { Inject, Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import Swal from "sweetalert2";
import * as moment from "moment";
import { HttpClient } from "@angular/common/http";
import { environment } from "../../../environments/environment";
import * as XLSX from "xlsx";
import { DeviceDetectorService } from "ngx-device-detector";
import { NavigationEnd, NavigationError, NavigationStart, Router } from "@angular/router";
import { DOCUMENT } from "@angular/common";
import slugify from "slugify";
import { CoreConfigService } from "@core/services/config.service";
import { DomSanitizer } from "@angular/platform-browser";
import * as CryptoJS from "crypto-js";
import { CommunicationsService } from "./communications.service";
import { AngularEditorConfig } from "@kolkov/angular-editor";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { AuthenticationService } from "./authentication.service";
import { BehaviorSubject, of, Subject } from "rxjs";
import { saveAs } from "file-saver";
import { locale as italian } from "../../i18n/it";
import { ToastrService } from "ngx-toastr";
import { INoticeTimesObject, INoticeTimesString } from "app/modules/recruitment/candidates/models/candidates";
import { catchError, tap } from "rxjs/operators";

@Injectable({
  providedIn: "root",
})
export class UtilsService {
  public _unsubscribeAll: Subject<any>;
  public isMobile: boolean = false;
  public isTablet: boolean = false;
  public isDesktop: boolean = false;
  public isSafari: boolean = false;
  public isLargeDesktop: boolean = false;
  public isSmallDesktop: boolean = false;
  public deviceInfo: {
    userAgent: string;
    os: string;
    browser: string;
    device: string;
    os_version: string;
    browser_version: string;
    deviceType: string;
    orientation: string;
  };
  public AttachmentViewerURL: BehaviorSubject<string> = new BehaviorSubject("");
  public AttachmentViewerFileName: BehaviorSubject<string> = new BehaviorSubject("");
  public pageIsLoading: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public themeConfiguration: any = {};
  public isMaintenance: boolean = false;

  public absencePopupOpen: { holidays: boolean; permissions: boolean; sickness: boolean; addAbsence: boolean } = {
    holidays: false,
    permissions: false,
    sickness: false,
    addAbsence: false,
  };

  public popupOpenTicketIsOpen: boolean = false;

  public popupOpenTaskIsOpen: boolean = false;
  public popupOpenProductForm: boolean = false;

  public defaultEditorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: "auto",
    minHeight: "150px",
    maxHeight: "auto",
    width: "auto",
    minWidth: "0",
    translate: "yes",
    enableToolbar: true,
    showToolbar: true,
    placeholder: "Inserisci testo...",
    defaultParagraphSeparator: "",
    defaultFontName: "",
    defaultFontSize: "",
    fonts: [],
    uploadWithCredentials: false,
    sanitize: false,
    toolbarPosition: "top",
    toolbarHiddenButtons: [],
  };

  constructor(
    private _translator: TranslateService,
    private _httpClient: HttpClient,
    private _deviceService: DeviceDetectorService,
    private router: Router,
    @Inject(DOCUMENT) private document,
    private _coreConfigService: CoreConfigService,
    private sanitizer: DomSanitizer,
    private _notificationService: CommunicationsService,
    private _modalService: NgbModal,
    private _authService: AuthenticationService,
    private toastrService: ToastrService,
  ) {
    // Set the private defaults
    this._unsubscribeAll = new Subject();
    moment.locale("it");
    this.deviceInfo = this._deviceService.getDeviceInfo();
    this.isMobile = this._deviceService.isMobile();
    this.isTablet = this._deviceService.isTablet();
    this.isDesktop = this._deviceService.isDesktop();
    this.isSafari = this._deviceService.browser === "Safari";

    this.router.events.subscribe((event: any) => {
      if (!event?.url?.includes("auth") && !event?.url?.includes("?")) {
        if (!this.document.querySelector("body").classList.contains("not-auto-hide-loading")) {
          if (event instanceof NavigationStart) {
            this.showLoading();
            this.loadOptions();
          } else if (event instanceof NavigationEnd || event instanceof NavigationError) {
            setTimeout(() => this.hideLoading(), 100);
          }
        }
        if (event instanceof NavigationEnd || event instanceof NavigationError) {
          this._modalService.dismissAll();
        }
      }
    });

    window.addEventListener("resize", () => {
      if (window.innerWidth < 768) {
        this.isMobile = true;
        this.isTablet = false;
        this.isDesktop = false;
      } else if (window.innerWidth > 1180) {
        this.isMobile = false;
        this.isTablet = false;
        this.isDesktop = true;
      } else {
        this.isMobile = false;
        this.isTablet = true;
        this.isDesktop = false;
      }

      this.isLargeDesktop = window.innerWidth >= 1440;
      this.isLargeDesktop = !this.isLargeDesktop;
    });

    // prendo la sessione dell'utente corrente
    const user: any = this._authService.currentUserValue;
    // se la sessione esiste ed ha finito l'onboarding faccio le chiamate nelle notifiche
    if (user && user.send_onboarding === 1) {
      setInterval(async () => {
        await this._notificationService.getMine();
      }, 61 * 1000);
    }
  }

  public toggleAbsencePopup(name: string) {
    this.absencePopupOpen[name] = !this.absencePopupOpen[name];
  }

  showLoading() {
    this.pageIsLoading.next(true);
  }

  hideLoading() {
    setTimeout(() => {
      this.document.querySelector("body").classList.remove("not-auto-hide-loading");
      this.pageIsLoading.next(false);
    });
  }

  disableAutoHideLoading() {
    this.document.querySelector("body").classList.add("not-auto-hide-loading");
  }

  public async translate(str: string) {
    return this._translator
      .get(str)
      .toPromise()
      .catch((e) => {
        console.error(e);
        return str;
      });
  }

  public stripHTML(str: string) {
    return str
      .replace(/<br \/>/gi, "&nbsp;")
      .replace(/<br\/>/gi, "&nbsp;")
      .replace(/<br>/gi, "&nbsp;")
      .replace(/<.*?>/g, "")
      .replace("&euro;", "€");
  }

  public async showSWAL(options: any) {
    this.hideLoading();
    let args: any = {
      title: await this.translate("ALERT." + options.title),
      icon: options.icon,
      showConfirmButton: typeof options.showConfirmButton !== "boolean" ? true : options.showConfirmButton,
      showCancelButton: typeof options.showCancelButton !== "boolean" ? true : options.showCancelButton,
      cancelButtonText: await this.translate("ALERT." + (options.cancelButtonText || "BTN_CANCEL")),
      confirmButtonText: await this.translate("ALERT." + options.confirmButtonText || "BTN_SUCCESS"),
      customClass: {
        confirmButton: options.customClass?.confirmButton || "btn btn-success",
        cancelButton: options.customClass?.cancelButton || "btn btn-secondary ml-1",
      },
    };
    if (options.text) {
      args.text = await this.translate((options.text.includes("ERRORS.") ? "" : "ALERT.") + options.text);
    }
    if (options.html) {
      args.html = await this.translate((options.text.includes("ERRORS.") ? "" : "ALERT.") + options.html);
    }

    return Swal.fire(args);
  }

  public async showSWALDecision(options: any) {
    options = {
      title: "CONFIRM_DELETE_TITLE",
      icon: "warning",
      confirmButtonText: "CONFIRM_DELETE_BTN",
      customClass: {
        confirmButton: "btn btn-warning",
      },
      ...options,
    };

    const args: any = {
      title: await this.translate("ALERT." + options.title),
      text: await this.translate(options.text),
      icon: options.icon,
      showConfirmButton: typeof options.showConfirmButton !== "boolean" ? true : options.showConfirmButton,
      showCancelButton: typeof options.showCancelButton !== "boolean" ? true : options.showCancelButton,
      cancelButtonText: await this.translate("ALERT." + (options.cancelButtonText || "BTN_CANCEL")),
      confirmButtonText: await this.translate("ALERT." + options.confirmButtonText || "BTN_SUCCESS"),
      customClass: {
        confirmButton: options.customClass?.confirmButton || "btn btn-success",
        cancelButton: options.customClass?.cancelButton || "btn btn-secondary ml-1",
      },
    };
    return Swal.fire(args);
  }

  public async showSWALDeleteConfirmation(options: any) {
    options = {
      title: "CONFIRM_DELETE_TITLE",
      icon: "warning",
      confirmButtonText: "CONFIRM_DELETE_BTN",
      customClass: {
        confirmButton: "btn btn-danger",
      },
      ...options,
    };

    const args: any = {
      title: await this.translate("ALERT." + options.title),
      text: await this.translate(options.text),
      icon: options.icon,
      showConfirmButton: typeof options.showConfirmButton !== "boolean" ? true : options.showConfirmButton,
      showCancelButton: typeof options.showCancelButton !== "boolean" ? true : options.showCancelButton,
      cancelButtonText: await this.translate("ALERT." + (options.cancelButtonText || "BTN_CANCEL")),
      confirmButtonText: await this.translate("ALERT." + options.confirmButtonText || "BTN_SUCCESS"),
      customClass: {
        confirmButton: options.customClass?.confirmButton || "btn btn-success",
        cancelButton: options.customClass?.cancelButton || "btn btn-secondary ml-1",
      },
    };
    return Swal.fire(args);
  }

  public async showSWALEmptyField(options: any) {
    const args: any = {
      title: await this.translate("ALERT.ALERT_TITLE"),
      text: await this.translate(options.text),
      icon: "warning",
      showConfirmButton: true,
      showCancelButton: false,
      cancelButtonText: await this.translate("ALERT." + (options.cancelButtonText || "BTN_CANCEL")),
      confirmButtonText: await this.translate("ALERT.BTN_CLOSE"),
      customClass: {
        confirmButton: options.customClass?.confirmButton || "btn btn-success",
        cancelButton: options.customClass?.cancelButton || "btn btn-secondary ml-1",
      },
    };
    return Swal.fire(args);
  }

  public async showSWALError(options: any) {
    const args: any = {
      title: "ALERT_TITLE",
      text: "",
      icon: "warning",
      showConfirmButton: true,
      showCancelButton: false,
      confirmButtonText: "BTN_CLOSE",
      cancelButtonText: "",
      ...options,
    };
    return this.showSWAL(args);
  }

  public async showSWALInfo(options: any) {
    const args: any = {
      title: "ABORTED_TITLE",
      text: "",
      icon: "info",
      showConfirmButton: true,
      showCancelButton: false,
      confirmButtonText: "BTN_CLOSE",
      cancelButtonText: "",
      customClass: {
        confirmButton: options.customClass?.confirmButton || "btn btn-info",
      },
      ...options,
    };
    return this.showSWAL(args);
  }

  public async showSWALSuccess(options: any) {
    const args: any = {
      title: "COMPLETED_TITLE",
      text: "",
      icon: "success",
      showConfirmButton: true,
      showCancelButton: false,
      confirmButtonText: "BTN_CLOSE",
      cancelButtonText: "",
      ...options,
    };
    return this.showSWAL(args);
  }

  public async showPopupItemDeleted(text: string = "DELETED_TEXT") {
    await this.showSWAL({
      title: "DELETED_TITLE",
      text,
      icon: "success",
      confirmButtonText: "BTN_CLOSE",
      showCancelButton: false,
      customClass: {
        confirmButton: "btn btn-success",
      },
    });
  }

  public formatDate(date: any, format: string = "DD/MM/YYYY") {
    if (!date) return "";
    if (typeof date !== "string") {
      return date.format(format);
    }
    let retDate: any = moment(date);
    return retDate._isValid ? retDate.format(format) : "";
  }

  public formatBytes(bytes: number, decimals: number = 2) {
    if (!+bytes) return "0 Bytes";

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  }

  convertFileToBase64(event: any, cb: (event: any) => void) {
    if (event.target.files && event.target.files[0]) {
      let reader = new FileReader();

      reader.onload = (e: any) => {
        cb(e);
      };

      reader.readAsDataURL(event.target.files[0]);
    }
  }

  async upload_media(bucket: string, filename: string, file: any, oldUrl: string = "") {
    return this._httpClient.post(`${environment.API_BASEURL}/media/upload`, { bucket, filename, file, oldUrl }).toPromise();
  }

  async basic_get(endpoint: string) {
    return this._httpClient.get(endpoint).toPromise();
  }

  async basic_post(endpoint: string, data: any) {
    return this._httpClient.post(endpoint, data).toPromise();
  }

  async document_file(fileName: string) {
    this.showLoading();
    const options: any = { headers: new Headers({ Accept: "application/blob" }), responseType: "blob" };
    return this._httpClient.post("/view-file", { fileName }, options).subscribe(
      (file: any) => {
        this.hideLoading();
        const downloadedFileName: string = fileName.split("/").pop();
        return saveAs(file, downloadedFileName);
      },
      (err) => {
        this.hideLoading();
        console.error("err", err);
        console.error("Download Error:", err);
      },
    );
  }

  async getFile(fileName: string) {
    fileName = fileName.toString().replace(/\//gi, "|");
    const options: any = { headers: new Headers({ Accept: "application/blob" }), responseType: "blob" };

    this._httpClient.post<any>("/view-file", { fileName }, options).pipe(
      tap((file) => {
        const objectURL = window.URL.createObjectURL(file);
        this.AttachmentViewerFileName.next(fileName);
        this.AttachmentViewerURL.next(objectURL);
      }),
      catchError((err) => {
        console.error("Error downloading file:", err);
        return of(null);  // Restituisce un Observable nullo per continuare il flusso senza errori
      })
    ).subscribe();
  }

  calculateDifferenceBetweenDates(dataFrom: string, dataTo: string, uniteOfTime: any = "days") {
    const a = moment(dataTo);
    const b = moment(dataFrom);
    return Number(a.diff(b, uniteOfTime));
  }

  public exportAsExcelFile(file: any, excelFileName: string) {
    // controllo che dati passati esistano, altrimenti non vado avanti
    if (file.length < 1) return false;
    // creo una nuova pagina
    const workbook = XLSX.utils.book_new();
    // creo il contenuto della pagina passandogli l'array
    const worksheet = XLSX.utils.json_to_sheet(file);
    // setto la larghezza delle colonne
    worksheet["!cols"] = this.xlsxFitToColumn(file);
    // aggiungo il contenuto alla pagina e do il nome alal pagina
    XLSX.utils.book_append_sheet(workbook, worksheet, excelFileName);
    // prendo il nome del file e gli aggiungo il formato
    excelFileName += moment().format("_DD-MM-YYYY") + ".xlsx";
    // salva il file Excel
    XLSX.writeFile(workbook, excelFileName);
  }

  public xlsxFitToColumn(file) {
    // la seguente funzione setta la larghezza delle colonne
    let widthArr = Object.keys(file[0]).map((key) => {
      return { width: key.length + 6 }; // plus 2 to account for short object keys
    });
    for (let i = 0; i < file.length; i++) {
      let value = Object.values(file[i]);
      for (let j = 0; j < value.length; j++) {
        if (value[j] !== null && String(value[j]).length > widthArr[j].width) {
          widthArr[j].width = String(value[j]).length;
        }
      }
    }
    return widthArr;
  }

  public calculateFiscalCode(firstname: string, lastname: string, sex: string, birthday: string, birth_place: number) {
    return this.basic_post("/calcola-codice-fiscale", { firstname, lastname, sex, birthday, birth_place });
  }

  public parseXLSX(fileData: any, keys: Array<string>, xlsx_opts: any, cb: (event: any) => void, page: number | string = 0) {
    let fileReader = new FileReader();
    fileReader.onload = () => {
      const buffer = fileReader.result as ArrayBuffer;
      const data = new Uint8Array(buffer);
      let arr = [];
      for (let i = 0; i != data.length; ++i) arr[i] = String.fromCharCode(data[i]);
      const bstr = arr.join("");
      const workbook = XLSX.read(bstr, { type: "binary" });
      const firstSheetName = typeof page === "string" ? page : workbook.SheetNames[page];
      const worksheet = workbook.Sheets[firstSheetName];

      const dirtyData: any = XLSX.utils.sheet_to_json(worksheet, xlsx_opts);
      const cleanData: Array<object> = [];

      dirtyData.map((item: any) => {
        const values: any = Object.values(item);

        const obj: any = {};
        keys.map((key: string, i: number) => {
          let v: any = values[i];
          if (typeof v === "number" && Number(v) === v && v % 1 !== 0) {
            v = parseFloat(v.toFixed(2));
          }
          obj[key] = v;
        });
        cleanData.push(obj);
      });

      cb(cleanData);
    };
    return fileReader.readAsArrayBuffer(fileData);
  }

  public addDotsToNumber(str: any) {
    if (typeof str === "string") {
      if (str.includes(",") && str.includes(".")) {
        str = str.replace(".", "").replace(",", ".");
      } else if (str.includes(",") && !str.includes(".")) {
        str = str.replace(",", ".");
      } else if (!str.includes(",") && !str.includes(".")) {
        str = str + ".00";
      }
      str = Number(str);
    }
    str = isNaN(str) ? 0 : str;
    str = str.toString().replace(/,/gi, ".");
    try {
      const exp: any = parseFloat(str).toFixed(2).toString().split(".");
      return exp[0].toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".") + "," + (exp[1].length < 1 ? exp[1] + "0" : exp[1]);
    } catch (e) {
      return str;
    }
  }

  public removeDotsFromNumber(str: string): number {
    if(typeof str !== 'string') return str
    str = str.replace(".", "").replace(",", ".");
    return parseFloat(str);
  }

  public ucFirst(str: string): string {
    if (!!str) {
      return str[0].toUpperCase() + str.substring(1);
    }
  }

  async notice_values(type: string = "array"): Promise<INoticeTimesObject[] | INoticeTimesString> {
    const days: any = await this.translate("DAYS");
    const month: any = await this.translate("MONTH");
    const months: any = await this.translate("MONTHS");
    const and: any = await this.translate("AND");

    if (type === "array") {
      return [
        { days: 0, label: "Immediato" },
        { days: 10, label: "10 " + days },
        { days: 15, label: "15 " + days },
        { days: 20, label: "20 " + days },
        { days: 30, label: "1 " + month },
        { days: 45, label: `1 ${month} ${and} 15 ${days}` },
        { days: 60, label: "2 " + months },
        { days: 75, label: `2  ${month} ${and} 15 ${days}` },
        { days: 90, label: "3 " + months },
        { days: 105, label: `3  ${month} ${and} 15 ${days}` },
        { days: 120, label: "4 " + months },
        { days: 135, label: `4  ${month} ${and} 15 ${days}` },
        { days: 150, label: "5 " + months },
        { days: 165, label: `5  ${month} ${and} 15 ${days}` },
        { days: 180, label: "6 " + months },
      ];
    } else {
      return {
        "0": "Immediato",
        "10": "10 " + days,
        "15": "15 " + days,
        "20": "20 " + days,
        "30": "1 " + month,
        "45": `1 ${month} ${and} 15 ${days}`,
        "60": "2 " + months,
        "75": `2  ${month} ${and} 15 ${days}`,
        "90": "3 " + months,
        "105": `3  ${month} ${and} 15 ${days}`,
        "120": "4 " + months,
        "135": `4  ${month} ${and} 15 ${days}`,
        "150": "5 " + months,
        "165": `5  ${month} ${and} 15 ${days}`,
        "180": "6 " + month,
      };
    }
  }

  easterDate() {
    const year: number = moment().year();
    const a: number = year % 19;
    const b: number = Math.floor(year / 100); // controllare se serve fare un floor a numbero intero
    const c: number = year % 100;
    const p: number = (19 * a + b - Math.floor(b / 4) - Math.floor((b - Math.floor((b + 8) / 25) + 1) / 3) + 15) % 30;
    const q: number = (32 + 2 * ((b % 4) + Math.floor(c / 4)) - p - (c % 4)) % 7;
    const r: number = p + q - 7 * Math.floor((a + 11 * p + 22 * q) / 451) + 114;
    const month: number = Math.floor(r / 31);
    const day: number = (r % 31) + 1;

    const easter: string = year + "-" + month.toString().padStart(2, "0") + "-" + day.toString().padStart(2, "0");
    return moment(easter);
  }

  getHolidaysList() {
    const year: number = moment().year();
    return [
      `01/01/${year}`,
      `06/01/${year}`,
      `25/04/${year}`,
      `01/05/${year}`,
      `02/06/${year}`,
      `15/08/${year}`,
      `01/11/${year}`,
      `08/12/${year}`,
      `25/12/${year}`,
      `26/12/${year}`,
      this.easterDate().add(1, "day").format("DD/MM/YYYY"),
    ];
  }

  isHoliday(date: any) {
    date = typeof date === "string" ? moment(date) : date;
    return this.getHolidaysList().includes(date.format("DD/MM/YYYY"));
  }

  slugify_me(str: string) {
    return slugify(str, { remove: undefined, lower: true, locale: "it", strict: true, trim: true });
  }

  dateIsInThreeDays(date: any) {
    date = moment(date);
    const today: any = moment();
    return date.diff(today, "days") < 3;
  }

  async includeFile(fileName: string) {
    fileName = fileName.toString().replace(/\//gi, "|");
    const options: any = { headers: new Headers({ Accept: "application/blob" }), responseType: "blob" };
    return this._httpClient
      .post("/view-file", { fileName }, options)
      .toPromise()
      .then((data: any) => {
        const unsafeImageUrl = URL.createObjectURL(data);
        return this.sanitizer.bypassSecurityTrustResourceUrl(unsafeImageUrl);
      })
      .catch((err: any) => {
        console.error("err", err);
        console.error("Download Error:", err);
        return err;
      });
  }

  encryptData(password: string) {
    try {
      return CryptoJS.AES.encrypt(password, environment.PASS_KEY, { mode: CryptoJS.mode.ECB, padding: CryptoJS.pad.Pkcs7 }).toString();
    } catch (e) {
      console.error(e);
    }
  }

  fixRal(value: any) {
    const splittedValue: any = value.split("");

    if (value.includes(",") && splittedValue[splittedValue.length - 3] === "," && value.includes(".") && splittedValue[2] === ".") {
      value = this.removeDotsFromNumber(value);
    } else {
      // se include il punto e il punto è usato a index 2 - rimuovo quel punto
      if (value.includes(".") && splittedValue[2] === ".") {
        value = value.split("");
        delete value[2];
        value = value.join("");
      }
      // se include la virgola e la virgola è alla index 2 - rimuovo quella virgola
      if (value.includes(",") && splittedValue[2] === ",") {
        value = value.split("");
        delete value[2];
        value = value.join("");
      }
      // se include la virgola ed è il separatore dei decimali
      if (value.includes(",") && splittedValue[splittedValue.length - 3] === ",") {
        value = value.split("");
        value[-2] = ".";
        value = value.join("");
      }
    }

    value = Number(value);

    return value;
  }

  async loadOptions() {
    if (!localStorage.getItem("token")) {
      return false;
    }

    this._coreConfigService.config.subscribe((params: any) => {
      this.themeConfiguration = { ...this.themeConfiguration, ...params.custom };
    });

    this.basic_get("/options")
      .then((result: any) => {
        Object.values(result).map((option: any) => {
          this.themeConfiguration[option.name] = option.value;
        });
      })
      .then(() => {
        if (moment().format("YYYY-MM-DD HH:mm") <= this.formatDate(this.themeConfiguration.maintenance_end, "YYYY-MM-DD HH:mm")) {
          this.isMaintenance = true;
        }
        //Logout Forzato in caso di manutenzione
        if (
          moment().format("YYYY-MM-DD HH:mm") >= this.formatDate(this.themeConfiguration.maintenance_start, "YYYY-MM-DD HH:mm") &&
          moment().format("YYYY-MM-DD HH:mm") <= this.formatDate(this.themeConfiguration.maintenance_end, "YYYY-MM-DD HH:mm")
           && !environment.enableMaintenanceLogin
        ) {
          const message: string = `${italian.data.PLATFORM_MAINTENACE_LOGIN_MESSAGE} ${this.formatDate(this.themeConfiguration.maintenance_start, "HH:mm")} ${italian.data.HOUR_TO.toLowerCase()} 
  ${this.formatDate(this.themeConfiguration.maintenance_end, "HH:mm")}`;
          alert(message);
          this._unsubscribeAll = new Subject();
          this._authService.logout();
          setTimeout(() => this.router.navigate(["/auth/login"]));
        }
      });
  }

  async updateOption(option: string, value: any, showSWALL: boolean = true) {
    return this._httpClient
      .put(`/options/${option}`, { value })
      .toPromise()
      .then(async (result: any) => {
        this.themeConfiguration[option] = value;
        if (showSWALL) {
          await this.showSWALSuccess({ text: "SUCCESS_UPDATE_OPTION" });
        }
        return result;
      })
      .catch(async (e) => {
        if (e === "Option cannot be updated") {
          await this.showSWALError({ text: "ERROR_OPTION_CANNOT_BE_UPDATED" });
        } else {
          await this.showSWALError({ text: "ERROR_UPDATE_OPTION" });
        }
        return e;
      });
  }

  //Funzione per controllare se un utente è effettivamente un collaboratore(Stagista, partita iva o prestazione occasionale)
  isACollaborator(user: any) {
    return (user.type_code === "collaborator" || user.type_code === "stage" || user.type_code === "occasional" || user.role === "Collaboratore");
  }

  async downloadPDF(source_fileName: string, destination_fileName: string) {
    return this._httpClient
      .post("/view-file", { fileName: source_fileName }, { responseType: "blob" })
      .toPromise()
      .then((data) => {
        const blob: Blob = new Blob([data], { type: data.type });
        destination_fileName = destination_fileName.includes(".pdf") ? destination_fileName : destination_fileName + ".pdf";
        return saveAs(blob, destination_fileName);
      })
      .catch((err) => console.error(err));
  }

  async showToastrInfo(message: string) {
    return this.translate("ALERT." + message)
      .then((messageTranslated: string) => {
        this.toastrService.info(messageTranslated, italian.data.ALERT.ALERT_TITLE, {
          toastClass: "toast ngx-toastr",
          closeButton: true,
        });
      });
  }

  async showToastrSuccess(message: string = null) {
    message = !!message ? message : "SUCCESS_DEFUALT_MESSAGE";
    const messageTranslated: string = await this.translate("ALERT." + message);
    this.toastrService.success(messageTranslated, italian.data.ALERT.COMPLETED_TITLE, {
      toastClass: "toast ngx-toastr",
      closeButton: true,
    });
  }

  async showToastrError(message: string = null) {
    let messageTranslated: string;
    if (!!message) {
      messageTranslated = await this.translate("ALERT." + message);
    } else {
      messageTranslated = italian.data.ALERT.ERROR_DEFUALT_MESSAGE;
    }
    this.toastrService.error(messageTranslated, italian.data.ALERT.ALERT_TITLE, {
      toastClass: "toast ngx-toastr",
      closeButton: true,
    });
  }

  moduleIsActive(moduleName: string) {
    return (this.themeConfiguration.installed_packages || []).includes(moduleName);
  }

  initModule(moduleName: string, languages: any, menu: any, settingPages: any) {
    this._coreConfigService.getConfig().subscribe(async (data: any) => {
      const installedPackages: string[] = data.custom.installed_packages || [];

      if (!installedPackages.includes(moduleName)) {
        const currentLanguages: any = data.custom.languages || [];
        const currentMenu: any = data.custom.menu || [];
        const currentSettingsPages: any = [...(data.custom.settings_pages || []), ...settingPages];

        currentLanguages.push(languages);
        currentMenu.push(menu);

        installedPackages.push(moduleName);

        this._coreConfigService.setConfig(
          {
            custom: {
              languages: currentLanguages,
              menu: currentMenu,
              installed_packages: installedPackages,
              settings_pages: currentSettingsPages,
            },
          },
          { emitEvent: true },
        );

        await this.loadOptions();
      }
    });
  }

  public fixPasteFromOtherEditorsInAngularEditor(availableTags: string[] = ["p", "h1", "span", "h2", "h3", "ul", "ol", "li", "b", "strong", "em", "i", "u", "a"]) {
    setTimeout(() => {
      const angularEditorTextAreas: any = document.querySelectorAll(".angular-editor-textarea");
      if (angularEditorTextAreas && angularEditorTextAreas.length > 0) {
        angularEditorTextAreas.forEach((item: any) => {
          item.addEventListener("paste", (e: any) => {
            e.preventDefault();
            const html = e.clipboardData.getData("text/html");
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, "text/html");
            const body = doc.querySelector("body");
            const elements = body.querySelectorAll("*");
            elements.forEach((element) => {
              if (!availableTags.includes(element.tagName.toLowerCase())) {
                element.remove();
              } else {
                element.removeAttribute("align");
                element.removeAttribute("class");
                element.removeAttribute("style");
              }
              element.textContent = element.textContent.trim();
            });
            let bodyWithoutComments: string = body.innerHTML
              .replace(/\t/g, "")
              .replace(/\n/g, "")
              .replace(/\r/g, "")
              .replace(/&nbsp;/g, "");
            while (bodyWithoutComments.includes("<!--") && bodyWithoutComments.includes("-->")) {
              const start: number = bodyWithoutComments.indexOf("<!--");
              const end: number = bodyWithoutComments.indexOf("-->") + 3;
              bodyWithoutComments = bodyWithoutComments.substring(0, start) + bodyWithoutComments.substring(end);
            }
            item.innerHTML += bodyWithoutComments;
          });
        });
      }
    });
  }

  calculatePercentageBetweenTwoNumbers(a: number, b: number) {
    if(a < 1 || b < 1) return 0
    return Number((((a - b) / a) * 100).toFixed(2));
  }

  objectsAreDifferent(obj1: any, obj2: any): boolean {
    // Confronta se entrambi gli oggetti sono nulli o non definiti
    if (obj1 === null || obj2 === null || typeof obj1 !== "object" || typeof obj2 !== "object") {
      return obj1 !== obj2;
    }

    // Confronta se gli oggetti hanno lo stesso numero di proprietà
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    if (keys1.length !== keys2.length) {
      return true;
    }

    // Confronta le proprietà degli oggetti
    for (let key of keys1) {
      if (!keys2.includes(key) || this.objectsAreDifferent(obj1[key], obj2[key])) {
        return true;
      }
    }

    return false;
  }
}
